
import Stepper from 'primevue/stepper';
import StepList from 'primevue/steplist';
import StepPanels from 'primevue/steppanels';
import StepItem from 'primevue/stepitem';
import Step from 'primevue/step';
import StepPanel from 'primevue/steppanel';

import IftaLabel from 'primevue/iftalabel';
import InputNumber from 'primevue/inputnumber';

import Card from 'primevue/card';
import Button from 'primevue/button';
import ProgressSpinner from 'primevue/progressspinner';
import SelectButton from 'primevue/selectbutton';
import Toast from 'primevue/toast';

import { computed, defineComponent, onMounted, onBeforeUnmount, ref } from 'vue';
import { useAuthStore } from '../../stores/auth';
import { storeToRefs } from 'pinia';
import { external } from '../../apis/external';
import { main_wallet } from '../../apis/main_wallet';
import { useWebStore } from '../../stores/web';
import { useToast } from 'primevue/usetoast';
import QRCode from 'qrcode';
import { showVuePopup } from '../../utils/showVuePopup';
import WalletUpdate from '../../popup/WalletUpdate.vue';
import { useSession } from '../../stores/session';

export default defineComponent({
	name: 'DepositP2P',
	components: {
		Stepper, StepList, StepPanels, StepItem, Step, StepPanel,
		IftaLabel, InputNumber,
		Card, Button, ProgressSpinner, SelectButton,
		Toast,
	},
	setup() {
		const auth = useAuthStore();
		auth.hydrate();
		const { profile } = storeToRefs(auth);

		const web = useWebStore();
		web.hydrate();
		const { webPermission } = storeToRefs(web);

		const toast = useToast();
		const session = useSession();

		const stepper = ref<string>('1');

		// เปิด/ปิดช่องทาง P2P แบบ reactive
		const isActive = computed<boolean>(() => webPermission.value?.isDeposit?.isPeer2Pay ?? false);

		// ต้อง sync P2P ก่อนใช้หรือไม่
		const isBankUpdate = computed<boolean>(() => {
			const synced = !!profile.value?.isSyncPeer2Pay;
			const hasUID = !!profile.value?.peer2PayUID;
			return !(synced && hasUID);
		});

		const form = ref({
			amount: 0,
			type: 'พื้นฐาน' as 'พื้นฐาน' | 'คิวอาโค้ด',
			btn: { disabled: false, loading: false },
		});

		const amountLimit = ref({ min: 0, max: 0 });

		const bankDeposit = ref({
			data: {
				contentQr: '',
				url: '',
				merchantName: '',
				quantity: 0,
				token: '',
			},
			active: false,
			loading: false,
		});

		const options = ref(['พื้นฐาน', 'คิวอาโค้ด']);

		const handlePeer2PayInfo = async () => {
			try {
				const resp = await external.getPeer2PayInfo();
				if (resp.code === '8017') {
					alert(resp.message);
					await auth.logout();
					window.location.reload();
					return;
				}
				if (resp.code !== '0') throw new Error(resp.message);

				form.value.amount = resp.result.minimumDeposit;
				amountLimit.value = {
					min: resp.result.minimumDeposit,
					max: resp.result.maximumDeposit,
				};
			} catch (error: any) {
				toast.add({ severity: 'error', summary: 'เกิดข้อผิดพลาดบางอย่าง', detail: error?.message || String(error), life: 3000 });
			}
		};

		const handlePeer2PayQrcode = async () => {
			if (form.value.amount < amountLimit.value.min || form.value.amount > amountLimit.value.max) {
				alert(`กรุณาระบุจำนวนเงินระหว่าง ${amountLimit.value.min} ถึง ${amountLimit.value.max}`);
				return;
			}

			bankDeposit.value.loading = true;
			try {
				const payload = {
					amount: form.value.amount
				}

				const resp = await main_wallet.p2pGenerateQr(payload);

				if (resp.code === '8017') {
					alert(resp.message);
					await auth.logout();
					window.location.reload();
					return;
				}
				if (resp.code !== '0') throw new Error(resp.message);

				const result = resp.result;
				const embeddedUrl = `${result.contentQr}&embedded=true`;

				const dataUrl = await QRCode.toDataURL(embeddedUrl, {
					errorCorrectionLevel: 'H',
					margin: 1,
				});

				bankDeposit.value.data = {
					contentQr: dataUrl,
					url: embeddedUrl,
					merchantName: result.merchantName,
					quantity: result.quantity,
					token: result.token,
				};
				bankDeposit.value.active = true;
			} catch (error: any) {
				toast.add({ severity: 'error', summary: 'เกิดข้อผิดพลาดบางอย่าง', detail: error?.message || String(error), life: 3000 });
			} finally {
				bankDeposit.value.loading = false;
			}
		};

		const handleSubmit = async () => {
			form.value.btn.loading = true;
			try {
				await handlePeer2PayQrcode();
				if (bankDeposit.value.active) {
					stepper.value = '2';
				}
			} catch (error: any) {
				toast.add({ severity: 'error', summary: 'เกิดข้อผิดพลาดบางอย่าง', detail: error?.message || String(error), life: 3000 });
			} finally {
				form.value.btn.loading = false;
			}
		};

		const btnClickUpdateP2p = async () => {
			session.set('navbar_primary', 'wallet');
			session.set('navbar_sub', 'wallet:update');
			await showVuePopup(WalletUpdate, { tabs: 'p2p' });
		};

		const handlerEvent = (e: MessageEvent) => {
			const evt = (e?.data as any)?.event;
			if (evt === 'p2p_deposit_complete') {
				stepper.value = '3';
			}
		};

		onMounted(() => {
			handlePeer2PayInfo();
			window.addEventListener('message', handlerEvent, false);
		});

		onBeforeUnmount(() => {
			window.removeEventListener('message', handlerEvent, false);
		});

		return {
			profile,
			stepper,
			form,
			options,
			amountLimit,
			bankDeposit,
			handleSubmit,
			isActive,
			isBankUpdate,
			btnClickUpdateP2p,
		};
	},
});
